import {
  AssetDeleteAction,
  CategoryItemAddAction,
  CategoryItemDeleteAction,
  CategoryItemListAction,
  CategoryItemUpdateAction,
  KalturaAsset,
  KalturaAssetReferenceType,
  KalturaCategoryItem,
  KalturaCategoryItemByIdInFilter,
  KalturaStringValue,
} from '@kaltura-ott/ott-client-types/api/types';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import { KuxKalturaCategoryItemArgs } from '../../interfaces';
import { kalturaClient } from '../kaltura-clent';
import { KalturaCategoryTags, KalturaMetas } from '../utils/tags';

import { createCategoryV2 } from './category-v2';

/**
 *
 * @param ids
 */
export const getCategoryByIds = (ids: string) => {
  const client = kalturaClient();
  const filter = new KalturaCategoryItemByIdInFilter({ idIn: ids });
  const action = new CategoryItemListAction({ filter: filter });
  return client.request(action).pipe(
    map((response) => {
      return response.objects;
    }),
    catchError((err) => {
      return of(err);
    }),
  );
};

/**
 *
 * @param categoryId
 */
export const getCategoryById = (categoryId: string | number) => {
  return getCategoryByIds(categoryId.toString()).pipe(map((categories) => categories[0]));
};

export interface CategoryChildParentConnection {
  childId?: string | number;
  childrenIds?: (string | number)[];
  parentId: string | number;
}

export const addChildCategoryToParent = (data: CategoryChildParentConnection) => {
  const client = kalturaClient();
  let newChildrenIds: (string | number)[] = [];
  if (data.childrenIds) {
    newChildrenIds = data.childrenIds;
  }
  if (data.childId) {
    newChildrenIds.push(data.childId);
  }

  return getCategoryById(data.parentId).pipe(
    mergeMap((parent) => {
      const childrenIds = [...parent.childrenIds.split(',').filter(Boolean), ...newChildrenIds].join(',');
      const action = new CategoryItemUpdateAction({
        id: parent.id,
        objectToUpdate: new KalturaCategoryItem({ childrenIds, multilingualName: parent.multilingualName }),
      });
      return client.request(action);
    }),
  );
};

/**
 * set assetId into dynamic data field
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const assignAssetToCategory = (category: any) => {
  if (!category.assetId) {
    return category;
  }
  category.dynamicData = {
    assetId: new KalturaStringValue({ value: category.assetId }),
  };
  delete category.assetId;
  return category;
};

export const updateCategory = (
  category: KalturaCategoryItem,
  updateParent = false,
): Observable<KalturaCategoryItem> => {
  const client = kalturaClient();
  client.appendDefaultRequestOptions({ language: '*' });
  const objectToUpdate: KalturaCategoryItem = new KalturaCategoryItem(category);
  objectToUpdate.allowEmptyObject('dynamicData');
  const action = new CategoryItemUpdateAction({
    id: category.id,
    objectToUpdate,
  });
  if (updateParent) {
    return client.request(action).pipe(
      mergeMap((resp) => {
        return addChildCategoryToParent({
          childId: resp.id,
          parentId: category.parentId,
        });
      }),
    );
  }

  return client.request(action).pipe(
    catchError((err) => {
      return of(err);
    }),
  );
};

/**
 *
 * @param category
 */
export const createRootCategory = (category: KuxKalturaCategoryItemArgs): Observable<KalturaCategoryItem> => {
  const _category = assignAssetToCategory(category);

  const client = kalturaClient();
  client.appendDefaultRequestOptions({ language: '*' });
  const action = new CategoryItemAddAction({
    objectToAdd: new KalturaCategoryItem(_category),
  });
  return client.request(action).pipe(
    catchError((err) => {
      return of(err);
    }),
  );
};

/**
 * Create category AND assign asset with template
 */
export const createChildCategory = (category: KuxKalturaCategoryItemArgs): Observable<KalturaCategoryItem> => {
  const parentId = category.parentId as string;
  delete category.parentId;

  return createRootCategory(category).pipe(
    mergeMap((childCategory) => {
      return addChildCategoryToParent({ parentId, childId: childCategory.id });
    }),
  );
};

/**
 * Delete category
 */
export const deleteCategory = (categoryId: number) => {
  const client = kalturaClient();
  client.appendDefaultRequestOptions({ language: '*' });
  const actions: Array<Observable<void | boolean>> = [
    client.request(
      new CategoryItemDeleteAction({
        id: categoryId,
      }),
    ),
  ];

  return getCategoryById(categoryId).pipe(
    mergeMap((category) => {
      if (category.dynamicData && category.dynamicData.assetId) {
        actions.push(
          client.request(
            new AssetDeleteAction({
              id: category.dynamicData.assetId.value,
              assetReferenceType: KalturaAssetReferenceType.media,
            }),
          ),
        );
      }

      return forkJoin(actions).pipe(
        catchError((err) => {
          return of(err);
        }),
      );
    }),
  );
};

/**
 * Create tree item
 */
export const createTreeItem = (
  category: KuxKalturaCategoryItemArgs,
  parentId: number,
  { tags, metas }: { tags: KalturaCategoryTags; metas: KalturaMetas },
): Observable<KalturaCategoryItem> => {
  let categoryItem: KalturaCategoryItem;
  const categoryAsset = new KalturaAsset();

  categoryAsset.tags = tags || {};
  categoryAsset.metas = metas || {};

  return createCategoryV2(category, categoryAsset).pipe(
    mergeMap((result) => {
      if (result.hasErrors()) {
        return throwError(result[0].error || result[1].error);
      }

      categoryItem = result[0] && !result[0].error ? result[0].result : null;

      return addChildCategoryToParent({ parentId, childId: categoryItem.id }).pipe(
        map(() => categoryItem),
        catchError((err) => {
          return of(err);
        }),
      );
    }),
    catchError((err) => {
      return of(err);
    }),
  );
};
